import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { signIn, signOut, buildAuthUser, type AuthUser } from '../services/authService';

// Define context type
type AuthContextType = {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate user from the current Supabase session (if any).
  const refreshSession = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(await buildAuthUser(session.user));
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Session refresh error:', err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Restore session on mount and react to auth state changes (sign-in/out,
  // token refresh, recovery links).
  //
  // IMPORTANT: never call other supabase methods (e.g. supabase.from(...))
  // synchronously inside the onAuthStateChange callback — supabase-js holds an
  // auth lock during the callback, and a data call that needs the session token
  // would deadlock it (symptom: stuck on "Signing in..."). We defer the profile
  // load to a microtask so it runs after the lock is released.
  useEffect(() => {
    refreshSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      const sessionUser = session.user;
      setTimeout(async () => {
        try {
          setUser(await buildAuthUser(sessionUser));
        } catch (err) {
          console.error('Profile load error:', err);
        } finally {
          setIsLoading(false);
        }
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Login via Supabase Auth.
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const authUser = await signIn(email, password);
      if (!authUser) return false;
      setUser(authUser);
      return true;
    } catch (err) {
      console.error('Login error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
